import { useAppMentionOptions } from "@/hooks/use-app-mention-options";
import { Button, Checkbox, Text } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useTiptapMentions } from "@/hooks/useTiptapMentions";
import {
  getUsersOptions,
  useCreateMatterNote,
  useUpdateMatterNote,
} from "@/lib/data/react-query";
import {
  type Matter,
  type MatterNote,
  type MatterSubItemType,
} from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { EditorContent } from "@tiptap/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

type MatterNoteComposerProps = {
  matter: Matter;
  massTortId?: string;
  selectedDraft?: MatterNote | null;
  onDraftProcessed?: () => void;
  subItem?: {
    id: string;
    type: MatterSubItemType;
  };
  onCreateSuccess?: () => void;
  defaultCreatePinned?: boolean;
};

type SubItemPayload = Partial<{
  vehicleId: string;
  incidentReportId: string;
  insuranceId: string;
  medicalTreatmentId: string;
  medicalTreatmentBillId: string;
  medicalTreatmentAppointmentId: string;
  medicalTreatmentRecordId: string;
  medicalTreatmentPlanId: string;
  medicalTreatmentBalanceConfirmationId: string;
  settlementDemandId: string;
  settlementOfferId: string;
  settlementCounterId: string;
  expenseId: string;
  lostWagesId: string;
  lienId: string;
  propertyDamageId: string;
  signatureRequestId: string;
  trustCheckId: string;
  litigationDateId: string;
  statuteOfLimitationsId: string;
  fileId: string;
  emailId: string;
}>;

const buildSubItemPayload = (
  subItem?: MatterNoteComposerProps["subItem"],
): SubItemPayload => {
  if (!subItem) {
    return {};
  }

  switch (subItem.type) {
    case "vehicle":
      return { vehicleId: subItem.id };
    case "incident-report":
      return { incidentReportId: subItem.id };
    case "insurance":
      return { insuranceId: subItem.id };
    case "medical-treatment":
      return { medicalTreatmentId: subItem.id };
    case "medical-treatment-bill":
      return { medicalTreatmentBillId: subItem.id };
    case "medical-treatment-appointment":
      return { medicalTreatmentAppointmentId: subItem.id };
    case "medical-treatment-record":
      return { medicalTreatmentRecordId: subItem.id };
    case "medical-treatment-plan":
      return { medicalTreatmentPlanId: subItem.id };
    case "medical-treatment-balance-confirmation":
      return { medicalTreatmentBalanceConfirmationId: subItem.id };
    case "settlement-demand":
      return { settlementDemandId: subItem.id };
    case "settlement-offer":
      return { settlementOfferId: subItem.id };
    case "settlement-counter":
      return { settlementCounterId: subItem.id };
    case "expense":
      return { expenseId: subItem.id };
    case "lost-wage":
      return { lostWagesId: subItem.id };
    case "lien":
      return { lienId: subItem.id };
    case "property-damage":
      return { propertyDamageId: subItem.id };
    case "signature-request":
      return { signatureRequestId: subItem.id };
    case "trust-check":
      return { trustCheckId: subItem.id };
    case "matter-litigation-date":
      return { litigationDateId: subItem.id };
    case "statute-of-limitations":
      return { statuteOfLimitationsId: subItem.id };
    case "file":
      return { fileId: subItem.id };
    case "email":
      return { emailId: subItem.id };
    default:
      return {};
  }
};

export const MatterNoteComposer = ({
  matter,
  massTortId,
  selectedDraft,
  onDraftProcessed,
  subItem,
  onCreateSuccess,
  defaultCreatePinned = false,
}: MatterNoteComposerProps) => {
  const { currentUser } = useUser();
  const annotationParentPayload = useMemo(
    () => (massTortId ? { massTortId } : { matterId: matter.id }),
    [massTortId, matter.id],
  );
  const [noteDraft, setNoteDraft] = useState("");
  const [isPinned, setIsPinned] = useState(defaultCreatePinned);
  const [isDraft, setIsDraft] = useState(false);
  const draftSavedRef = useRef(false);
  const pinnedCheckboxId = useId();
  const draftCheckboxId = useId();

  const { data: usersResponse, isLoading: isUsersLoading } =
    useQuery(getUsersOptions());
  const mentionUsers = useMemo(
    () => usersResponse?.data ?? [],
    [usersResponse],
  );
  const mentionOptions = useAppMentionOptions(
    mentionUsers,
    !massTortId,
    selectedDraft?.mentionedApps,
  );

  const createMatterNote = useCreateMatterNote();
  const updateMatterNote = useUpdateMatterNote();
  const draftContextRef = useRef<{
    noteDraft: string;
    isPinned: boolean;
    isDraft: boolean;
    getBackendMessage: () => string;
    currentUser: typeof currentUser;
    annotationParentPayload: { matterId: string } | { massTortId: string };
    subItem?: MatterNoteComposerProps["subItem"];
    editingDraft: MatterNote | null;
    loadedDraftId: string | null;
    createMatterNote: typeof createMatterNote;
    updateMatterNote: typeof updateMatterNote;
  }>({
    noteDraft: "",
    isPinned: defaultCreatePinned,
    isDraft: false,
    getBackendMessage: () => "",
    currentUser,
    annotationParentPayload,
    subItem,
    editingDraft: null as MatterNote | null,
    loadedDraftId: null as string | null,
    createMatterNote,
    updateMatterNote,
  });

  const { editor, getBackendMessage, setContent } = useTiptapMentions({
    plainText: true,
    mentionOptions,
    isLoading: isUsersLoading,
    onUpdate: (value) => {
      setNoteDraft(value);
    },
    className:
      "max-h-[240px] min-h-[120px] overflow-y-auto border-0 bg-transparent px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Add a note...",
  });

  useEffect(() => {
    if (noteDraft.trim()) {
      draftSavedRef.current = false;
    }
  }, [noteDraft]);

  useEffect(() => {
    draftContextRef.current = {
      ...draftContextRef.current,
      noteDraft,
      isPinned,
      isDraft,
      getBackendMessage,
      currentUser,
      annotationParentPayload,
      subItem,
      createMatterNote,
      updateMatterNote,
    };
  }, [
    noteDraft,
    isPinned,
    isDraft,
    getBackendMessage,
    currentUser,
    annotationParentPayload,
    subItem,
    createMatterNote,
    updateMatterNote,
  ]);

  useEffect(() => {
    if (!selectedDraft) {
      draftContextRef.current.editingDraft = null;
      draftContextRef.current.loadedDraftId = null;
      return;
    }

    if (draftContextRef.current.loadedDraftId === selectedDraft.id) {
      return;
    }

    draftContextRef.current.editingDraft = selectedDraft;
    draftContextRef.current.loadedDraftId = selectedDraft.id;
    const draftMessage = selectedDraft.message;
    setContent(draftMessage);
    setNoteDraft(draftMessage);
    setIsPinned(Boolean(selectedDraft.pinned));
    setIsDraft(Boolean(selectedDraft.draft));
  }, [selectedDraft, setContent]);

  useEffect(() => {
    draftSavedRef.current = false;
  }, [matter.id, massTortId]);

  useEffect(() => {
    return () => {
      const context = draftContextRef.current;
      if (draftSavedRef.current || !context.noteDraft.trim()) {
        return;
      }

      draftSavedRef.current = true;
      const editingDraft = context.editingDraft;
      const draftMessage = context.getBackendMessage();

      if (editingDraft) {
        context.updateMatterNote.mutate({
          ...context.annotationParentPayload,
          id: editingDraft.id,
          dateModified: editingDraft.dateModified,
          fileId: editingDraft.fileId,
          message: draftMessage,
          pinned: context.isPinned,
          draft: true,
        });
        return;
      }

      context.createMatterNote.mutate({
        ...context.annotationParentPayload,
        message: draftMessage,
        pinned: context.isPinned,
        draft: true,
        userId: context.currentUser.id,
        ...buildSubItemPayload(context.subItem),
      });
    };
  }, []);

  const handleSubmit = async () => {
    if (!noteDraft.trim()) {
      return;
    }

    const previousDraft = noteDraft;
    const previousPinned = isPinned;
    const previousIsDraft = isDraft;
    const draftMessage = getBackendMessage();
    const editingDraft = draftContextRef.current.editingDraft;

    setContent("");
    setNoteDraft("");
    setIsPinned(defaultCreatePinned);
    setIsDraft(false);

    try {
      if (editingDraft) {
        await updateMatterNote.mutateAsync({
          ...annotationParentPayload,
          id: editingDraft.id,
          dateModified: editingDraft.dateModified,
          fileId: editingDraft.fileId,
          message: draftMessage,
          draft: isDraft,
          pinned: isPinned,
          userId: currentUser.id,
        });
        draftContextRef.current.editingDraft = null;
        onDraftProcessed?.();
      } else {
        await createMatterNote.mutateAsync({
          ...annotationParentPayload,
          message: draftMessage,
          draft: isDraft,
          pinned: isPinned,
          userId: currentUser.id,
          ...buildSubItemPayload(subItem),
        });
      }
      draftSavedRef.current = true;
      onCreateSuccess?.();
    } catch {
      setContent(draftMessage);
      setNoteDraft(previousDraft);
      setIsPinned(previousPinned);
      setIsDraft(previousIsDraft);
    }
  };

  const isSubmitting = createMatterNote.isPending;
  const canSubmit = noteDraft.trim().length > 0 && !isSubmitting;

  return (
    <div className="border-border bg-card rounded-xl border shadow-sm">
      <EditorContent editor={editor} />
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
        <label
          htmlFor={pinnedCheckboxId}
          className="flex cursor-pointer items-center gap-2"
        >
          <Checkbox
            id={pinnedCheckboxId}
            checked={isPinned}
            disabled={isSubmitting}
            onCheckedChange={(checked) => {
              setIsPinned(checked === true);
            }}
          />
          <Text variant="body" color="muted">
            Pin note
          </Text>
        </label>
        <label
          htmlFor={draftCheckboxId}
          className="flex cursor-pointer items-center gap-2"
        >
          <Checkbox
            id={draftCheckboxId}
            checked={isDraft}
            disabled={isSubmitting}
            onCheckedChange={(checked) => {
              setIsDraft(checked === true);
            }}
          />
          <Text variant="body" color="muted">
            Draft
          </Text>
        </label>
        <div className="flex grow justify-end">
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit();
            }}
            data-testid="add-matter-note-button"
          >
            Add note
          </Button>
        </div>
      </div>
    </div>
  );
};
